import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { customerApi } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { useInquiryCategories, categoryLabel, TYPE_LABELS, formatWhen } from '../../components/inquiries/inquiryShared';

const STATUS_COLORS = { pending:'badge-gray', confirmed:'badge-navy', processing:'badge-navy', shipped:'badge-green', delivered:'badge-green', cancelled:'badge-red' };

export default function AccountPage() {
  const { logout, isLoggedIn } = useAuth();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('orders');
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [inquiries, setInquiries] = useState(null);
  const categories = useInquiryCategories();
  const navigate = useNavigate();

  useEffect(() => {
    if (tab === 'inquiries' && isLoggedIn) customerApi.get('/inquiries/mine').then(r => setInquiries(r.data)).catch(() => setInquiries([]));
  }, [tab, isLoggedIn]);

  useEffect(() => {
    if (!isLoggedIn) { navigate('/login'); return; }
    customerApi.get('/customers/me').then(r => { setData(r.data); setForm({ first_name: r.data.first_name, last_name: r.data.last_name, phone: r.data.phone || '', address_line1: r.data.address_line1 || '', city: r.data.city || '', state: r.data.state || '', zip: r.data.zip || '' }); }).catch(() => {});
  }, [isLoggedIn, navigate]);

  const saveProfile = async () => {
    try { await customerApi.put('/customers/me', form); setData(d => ({ ...d, ...form })); setEditing(false); } catch {}
  };

  if (!data) return <div className="loading"><div className="spinner" /></div>;

  return (
    <div>
      <div className="page-hero" style={{ padding: '32px 0' }}>
        <h1>My Account</h1>
        <p>Welcome back, {data.first_name}!</p>
      </div>
      <div className="container" style={{ padding: '36px 20px' }}>
        <div style={{ display: 'flex', gap: 32, alignItems: 'flex-start' }}>
          {/* Sidebar */}
          <aside style={{ width: 200, flexShrink: 0 }}>
            <div className="card" style={{ padding: 16 }}>
              <div style={{ fontWeight: 700, fontSize: 15, color: '#1a2744', marginBottom: 4 }}>{data.first_name} {data.last_name}</div>
              <div style={{ fontSize: 12, color: '#9a9a9a', marginBottom: 16 }}>{data.email}</div>
              {[['orders','My Orders'],['inquiries','My Inquiries'],['profile','Profile']].map(([val, label]) => (
                <button key={val} onClick={() => setTab(val)} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 12px', borderRadius: 8, border: 'none', background: tab === val ? 'rgba(201,162,39,0.1)' : 'transparent', color: tab === val ? '#c9a227' : '#555', fontWeight: tab === val ? 700 : 400, cursor: 'pointer', marginBottom: 4, fontSize: 14 }}>{label}</button>
              ))}
              <div style={{ borderTop: '1px solid #e8e8e8', marginTop: 8, paddingTop: 8 }}>
                <button onClick={() => { logout(); navigate('/'); }} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 12px', borderRadius: 8, border: 'none', background: 'transparent', color: '#c62828', fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>Sign Out</button>
              </div>
            </div>
          </aside>

          {/* Content */}
          <div style={{ flex: 1 }}>
            {tab === 'orders' && (
              <div>
                <h2 style={{ fontWeight: 800, color: '#1a2744', marginBottom: 20 }}>Order History</h2>
                {!data.orders?.length ? (
                  <div className="empty-state"><p>No orders yet.</p><Link to="/shop" className="btn btn-navy" style={{ marginTop: 16 }}>Start Shopping</Link></div>
                ) : (
                  <div>
                    {data.orders.map(order => (
                      <div key={order.order_number} className="card" style={{ padding: '16px 20px', marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: 15, color: '#1a2744', fontFamily: 'monospace' }}>{order.order_number}</div>
                          <div style={{ fontSize: 12, color: '#9a9a9a', marginTop: 2 }}>{new Date(order.created_at).toLocaleDateString()}</div>
                        </div>
                        <span className={`badge ${STATUS_COLORS[order.status] || 'badge-gray'}`} style={{ textTransform: 'capitalize' }}>{order.status}</span>
                        <div style={{ fontWeight: 800, fontSize: 16, color: '#1a2744' }}>${parseFloat(order.total).toFixed(2)}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {tab === 'inquiries' && (
              <div>
                <h2 style={{ fontWeight: 800, color: '#1a2744', marginBottom: 20 }}>My Inquiries</h2>
                {!inquiries ? <div className="spinner" /> : !inquiries.length ? (
                  <div className="empty-state"><p>No inquiries yet.</p><Link to="/sell" className="btn btn-navy" style={{ marginTop: 16 }}>Get a Quote</Link></div>
                ) : inquiries.map(inq => (
                  <Link key={inq.id} to={`/inquiry/${inq.access_token}`} className="card" style={{ display: 'block', padding: '16px 20px', marginBottom: 12, textDecoration: 'none' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 4 }}>
                      <span style={{ fontSize: 12, color: '#9a9a9a' }}>{TYPE_LABELS[inq.inquiry_type]} · {categoryLabel(categories, inq.item_category)}</span>
                      {inq.last_sender === 'staff' && <span className="badge badge-gold" style={{ fontSize: 10 }}>We replied</span>}
                    </div>
                    <p style={{ fontSize: 14, color: '#444', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{inq.item_description}</p>
                    <div style={{ fontSize: 11, color: '#9a9a9a', marginTop: 4 }}>Updated {formatWhen(inq.last_message_at || inq.created_at)}</div>
                  </Link>
                ))}
              </div>
            )}

            {tab === 'profile' && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                  <h2 style={{ fontWeight: 800, color: '#1a2744' }}>Profile</h2>
                  <button onClick={() => setEditing(e => !e)} className="btn btn-outline btn-sm">{editing ? 'Cancel' : 'Edit'}</button>
                </div>
                <div className="card" style={{ padding: 24 }}>
                  {editing ? (
                    <>
                      <div className="grid-2" style={{ gap: 16 }}>
                        <div className="form-group"><label className="form-label">First Name</label><input className="form-control" value={form.first_name} onChange={e => setForm(f => ({...f, first_name: e.target.value}))} /></div>
                        <div className="form-group"><label className="form-label">Last Name</label><input className="form-control" value={form.last_name} onChange={e => setForm(f => ({...f, last_name: e.target.value}))} /></div>
                      </div>
                      <div className="form-group"><label className="form-label">Phone</label><input className="form-control" value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))} /></div>
                      <div className="form-group"><label className="form-label">Address</label><input className="form-control" value={form.address_line1} onChange={e => setForm(f => ({...f, address_line1: e.target.value}))} /></div>
                      <div className="grid-2" style={{ gap: 16 }}>
                        <div className="form-group"><label className="form-label">City</label><input className="form-control" value={form.city} onChange={e => setForm(f => ({...f, city: e.target.value}))} /></div>
                        <div className="form-group"><label className="form-label">ZIP</label><input className="form-control" value={form.zip} onChange={e => setForm(f => ({...f, zip: e.target.value}))} /></div>
                      </div>
                      <button className="btn btn-primary" onClick={saveProfile}>Save Changes</button>
                    </>
                  ) : (
                    <div style={{ fontSize: 14, color: '#444', lineHeight: 2 }}>
                      <div><strong>Name:</strong> {data.first_name} {data.last_name}</div>
                      <div><strong>Email:</strong> {data.email}</div>
                      {data.phone && <div><strong>Phone:</strong> {data.phone}</div>}
                      {data.address_line1 && <div><strong>Address:</strong> {data.address_line1}, {data.city} {data.state} {data.zip}</div>}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
